import {
	createStore,
	createSubscriber,
	createContainer,
	createStateHook,
	createHook,
} from 'react-sweet-state';
import type {
	Action,
	BoundActions,
	HookFunction,
	HookStateFunction,
	OverrideContainerComponent,
	SubscriberComponent,
} from 'react-sweet-state';
import { graphql } from 'react-relay';

import { fg } from '@confluence/feature-gating';
import type { GraphQLTaggedNode } from 'react-relay';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { type UseReadFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { getEffectiveMultiMacroQueryBlocklist } from './useMultiMacroQuery.ts';
import type {
	multiMacroQueryWithAppKeyQuery$data,
	multiMacroQueryWithAppKeyQuery$variables,
} from './__generated__/multiMacroQueryWithAppKeyQuery.graphql.ts';
import type { multiMacroQueryQuery$data } from './__generated__/multiMacroQueryQuery.graphql.ts';

// Relay query for macros without appKey - used by preloader and will be used by this store after migration
export const MultiMacroQueryQuery: GraphQLTaggedNode = graphql`
	query multiMacroQueryQuery(
		$contentId: ID!
		$mode: MacroRendererMode = RENDERER
		$blocklist: [String!]
		$first: Int
		$after: String
		$refetchToken: String
	) @allowHigherTimeout {
		macros(
			contentId: $contentId
			blocklist: $blocklist
			first: $first
			after: $after
			refetchToken: $refetchToken
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				macroId
				contentId
				renderedMacro(mode: $mode) {
					appKey
					value
					macroRenderedRepresentation
					macroBodyStorage
					webResource {
						contexts
						keys
						superbatch {
							metatags
						}
						tags {
							css
							data
							js
						}
						uris {
							css
							data
							js
						}
					}
					mediaToken {
						token
					}
				}
			}
		}
	}
`;

// Relay query for macros with appKey - used by preloader and will be used by this store after migration
export const MultiMacroQueryWithAppKeyQuery: GraphQLTaggedNode = graphql`
	query multiMacroQueryWithAppKeyQuery(
		$contentId: ID!
		$cloudId: ID!
		$mode: MacroRendererMode = RENDERER
		$blocklist: [String!]
		$first: Int
		$after: String
		$staticFirst: Int
		$staticAfter: String
		$refetchToken: String
	) @allowHigherTimeout {
		macros(
			contentId: $contentId
			blocklist: $blocklist
			first: $first
			after: $after
			refetchToken: $refetchToken
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				macroId
				contentId
				renderedMacro(mode: $mode) {
					appKey
					value
					macroRenderedRepresentation
					macroBodyStorage
					webResource {
						contexts
						keys
						superbatch {
							metatags
						}
						tags {
							css
							data
							js
						}
						uris {
							css
							data
							js
						}
					}
					mediaToken {
						token
					}
				}
			}
		}
		staticMacros: confluence_staticMacros(
			contentId: $contentId
			cloudId: $cloudId
			first: $staticFirst
			after: $staticAfter
		) @optIn(to: ["AskInCcApiPlatformBeforeUsing"]) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				macroId
				value
				error {
					code
				}
			}
		}
	}
`;

type StaticMacroNode = {
	localId: string;
	value?: string | null;
	error?: { code: string } | null;
};

type StoreState = {
	contentId?: string;
	featureFlags?: Record<string, any>;
	revision?: string;
	complete: boolean;
	data: any;
	cursor?: string;
	macroData: Record<string, any>;
	error?: boolean;
	readFragmentInterop: UseReadFragmentInteropResult<any>;
	staticMacroData: Record<string, StaticMacroNode>;
	staticMacrosComplete: boolean;
	staticCursor?: string;
};

type ContainerParams = {
	contentId: string;
	cloudId?: string;
	contentRendererMode: string;
	featureFlags: Record<string, any>;
	cursor?: string;
	revision?: string;
	useServerRenderedData?: boolean;
	readFragmentInterop: UseReadFragmentInteropResult<any>;
};

const DEFAULT_PRELOAD_PAGE_SIZE = 8;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_STATIC_PRELOAD_PAGE_SIZE = 10;
const actions = {
	queryMacros:
		({
			contentId,
			cloudId,
			contentRendererMode,
			featureFlags,
			cursor,
			staticCursor,
			revision,
			useServerRenderedData = false,
			readFragmentInterop,
		}: {
			contentId: any;
			cloudId?: any;
			contentRendererMode: any;
			featureFlags: any;
			cursor: any;
			staticCursor: any;
			revision: any;
			useServerRenderedData?: boolean | undefined;
			readFragmentInterop: any;
		}): Action<StoreState> =>
		async ({ getState, setState, dispatch }) => {
			// avoid querying if required variables are not yet available
			if (!contentId) {
				return;
			}

			// avoid querying if already in progress
			if (
				getState().contentId === contentId &&
				getState().revision === revision &&
				getState().cursor === cursor &&
				getState().staticCursor === staticCursor
			) {
				return;
			}

			setState({
				contentId,
				revision,
				cursor,
				staticCursor,
				complete: false,
			});

			let mode = contentRendererMode.toUpperCase();
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (!process.env.REACT_SSR) {
				mode = window.location.pathname.startsWith('/wiki/pdf/') ? 'PDF' : mode;
			}

			const selectedQuery: GraphQLTaggedNode = MultiMacroQueryWithAppKeyQuery;

			const queryVariables: multiMacroQueryWithAppKeyQuery$variables = {
				contentId,
				cloudId: cloudId ?? '',
				mode,
				blocklist: getEffectiveMultiMacroQueryBlocklist(),
				first: !cursor ? DEFAULT_PRELOAD_PAGE_SIZE : DEFAULT_PAGE_SIZE,
				after: cursor,
				staticFirst: !staticCursor ? DEFAULT_STATIC_PRELOAD_PAGE_SIZE : DEFAULT_PAGE_SIZE,
				staticAfter: staticCursor,
			};

			// don't include revision if in SSR otherwise it will cause a cache miss between
			// SSR preload where revision is unknown and SSR render when this action is called
			if (!useServerRenderedData) {
				queryVariables.refetchToken = revision;
			}
			let data;
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			if (process.env.REACT_SSR && !getState().cursor) {
				data = readFragmentInterop({
					id: `$ROOT_QUERY.macros(${JSON.stringify({
						...(cursor ? { after: cursor } : {}),
						blocklist: queryVariables.blocklist,
						contentId,
						first: queryVariables.first,
						...(!useServerRenderedData ? { refetchToken: revision } : {}),
					})})`,
				});

				// have fragment take on same shape as query result
				if (data) {
					data = { macros: data } as any;
				}
			} else {
				const shouldUseServerRenderedData = useServerRenderedData && !getState().cursor;

				if (shouldUseServerRenderedData) {
					try {
						data = readFragmentInterop({
							id: `$ROOT_QUERY.macros(${JSON.stringify({
								...(cursor ? { after: cursor } : {}),
								blocklist: queryVariables.blocklist,
								contentId,
								first: queryVariables.first,
							})})`,
						});
						// have fragment take on same shape as query result
						if (data) {
							data = { macros: data } as any;
						}
					} catch (e) {
						//do not report error when query returns empty result in SSR
						if (e?.message && !e?.message.includes('Invariant Violation: 8')) {
							getMonitoringClient().submitError(e, {
								attribution: 'backbone',
							});
						}
					}
				}

				if (!data || !data?.macros || !shouldUseServerRenderedData) {
					const result = await queryInterop<
						multiMacroQueryQuery$data | multiMacroQueryWithAppKeyQuery$data,
						multiMacroQueryWithAppKeyQuery$variables
					>({
						query: selectedQuery,
						variables: queryVariables,
						context: {
							allowOnExternalPage: true,
						},
					});

					if (result.error) {
						getMonitoringClient().submitError(result.error, {
							attribution: 'backbone',
						});

						setState({
							error: true,
						});
					}

					data = result.data;
				}
			}

			const { nodes = [], pageInfo = {} } = data?.macros;
			const macros = nodes?.reduce((nodeMap, node) => {
				if (node) {
					const key = getMacrosCacheKey(node.contentId || '', node.macroId || '');
					nodeMap[key] = node?.renderedMacro;
				}
				return nodeMap;
			}, {});

			setState({
				macroData: {
					...getState().macroData,
					...macros,
				},
			});

			if (fg('conf_frontend_forge_static_macro_mvp')) {
				const { nodes: staticNodes = [], pageInfo: staticPageInfo } = data?.staticMacros ?? {};
				const staticMacros = staticNodes?.reduce(
					(nodeMap, node) => {
						if (node?.macroId) {
							nodeMap[node.macroId] = { ...node, localId: node.macroId } as StaticMacroNode;
						}
						return nodeMap;
					},
					{} as Record<string, StaticMacroNode>,
				);

				setState({
					staticMacroData: {
						...getState().staticMacroData,
						...staticMacros,
					},
				});

				const regularHasNextPage = pageInfo.hasNextPage;
				const staticHasNextPage = !!staticPageInfo?.hasNextPage;

				if (!regularHasNextPage && !staticHasNextPage) {
					setState({ complete: true, staticMacrosComplete: true });
				} else {
					if (!regularHasNextPage) setState({ complete: true });
					if (!staticHasNextPage) setState({ staticMacrosComplete: true });
				}

				// Fire a single dispatch covering whichever cursors need to advance,
				// avoiding two concurrent dispatches when both lists have more pages.
				if (regularHasNextPage || staticHasNextPage) {
					void dispatch(
						actions.queryMacros({
							contentId,
							cloudId,
							contentRendererMode,
							featureFlags,
							cursor: regularHasNextPage ? pageInfo.endCursor : cursor,
							staticCursor: staticHasNextPage ? staticPageInfo?.endCursor : staticCursor,
							revision,
							readFragmentInterop,
						}),
					);
				}
			} else {
				if (pageInfo.hasNextPage) {
					void dispatch(
						actions.queryMacros({
							contentId,
							cloudId,
							contentRendererMode,
							featureFlags,
							cursor: pageInfo.endCursor,
							staticCursor,
							revision,
							readFragmentInterop,
						}),
					);
				} else {
					setState({ complete: true });
				}
			}
		},
	//only for testing
	resetState:
		(): Action<StoreState> =>
		({ setState }) => {
			setState({
				complete: false,
				data: {},
				macroData: {},
				error: false,
				contentId: undefined,
				revision: undefined,
				cursor: undefined,
				staticMacroData: {},
				staticMacrosComplete: false,
				staticCursor: undefined,
			});
		},
};

const store = createStore<StoreState, typeof actions>({
	initialState: {
		complete: false,
		data: {},
		macroData: {},
		readFragmentInterop: () => null,
		staticMacroData: {},
		staticMacrosComplete: false,
	},
	actions,
	name: 'MultiMacroQueryStore',
});

export const MultiMacroQueryContainer: OverrideContainerComponent<ContainerParams> =
	createContainer(store, {
		onInit:
			() =>
			(
				{ dispatch },
				{
					contentId,
					cloudId,
					contentRendererMode,
					featureFlags,
					cursor,
					revision,
					useServerRenderedData,
					readFragmentInterop,
				}: ContainerParams,
			) => {
				void dispatch(
					actions.queryMacros({
						contentId,
						cloudId,
						contentRendererMode,
						featureFlags,
						cursor,
						staticCursor: undefined,
						revision,
						useServerRenderedData,
						readFragmentInterop,
					}),
				);
			},
		onUpdate:
			() =>
			(
				{ dispatch },
				{
					contentId,
					cloudId,
					contentRendererMode,
					featureFlags,
					cursor,
					revision,
					useServerRenderedData,
					readFragmentInterop,
				}: ContainerParams,
			) => {
				void dispatch(
					actions.queryMacros({
						contentId,
						cloudId,
						contentRendererMode,
						featureFlags,
						cursor,
						staticCursor: undefined,
						revision,
						useServerRenderedData,
						readFragmentInterop,
					}),
				);
			},
	});

function getMacrosCacheKey(contentId: string, macroId: string) {
	return `${contentId}::${macroId ?? 0}`;
}

function multiMacroQuerySelector(
	state: StoreState,
	params: { macroId: string; contentId: string },
) {
	const key = getMacrosCacheKey(params.contentId, params.macroId);
	const macroData = state.macroData?.[key];

	return {
		loading: !macroData && !state.complete,
		error: !macroData && state.error,
		renderedMacro: macroData || null,
		complete: state.complete,
	};
}

export const useMultiMacroQuery: HookStateFunction<
	{
		loading: boolean;
		error: boolean | undefined;
		renderedMacro: any;
		complete: boolean;
	},
	{
		macroId: string;
		contentId: string;
	}
> = createStateHook(store, {
	selector: multiMacroQuerySelector,
});

export const MultiMacroQuerySubscriber: SubscriberComponent<
	{
		loading: boolean;
		error: boolean | undefined;
		renderedMacro: any;
		complete: boolean;
	},
	BoundActions<
		StoreState,
		{
			queryMacros: ({
				contentId,
				cloudId,
				contentRendererMode,
				featureFlags,
				cursor,
				staticCursor,
				revision,
				useServerRenderedData,
				readFragmentInterop,
			}: {
				contentId: any;
				cloudId?: any;
				contentRendererMode: any;
				featureFlags: any;
				cursor: any;
				staticCursor: any;
				revision: any;
				useServerRenderedData?: boolean | undefined;
				readFragmentInterop: any;
			}) => Action<StoreState>;
			//only for testing
			resetState: () => Action<StoreState>;
		}
	>,
	{
		macroId: string;
		contentId: string;
	}
> = createSubscriber(store, {
	selector: multiMacroQuerySelector,
});

// exported for testing store actions
export const useMultiMacroQueryStoreHook: HookFunction<
	StoreState,
	BoundActions<
		StoreState,
		{
			queryMacros: ({
				contentId,
				cloudId,
				contentRendererMode,
				featureFlags,
				cursor,
				staticCursor,
				revision,
				useServerRenderedData,
				readFragmentInterop,
			}: {
				contentId: any;
				cloudId?: any;
				contentRendererMode: any;
				featureFlags: any;
				cursor: any;
				staticCursor: any;
				revision: any;
				useServerRenderedData?: boolean | undefined;
				readFragmentInterop: any;
			}) => Action<StoreState>;
			//only for testing
			resetState: () => Action<StoreState>;
		}
	>,
	void
> = createHook(store);
